import React from "react";
import PropTypes from "prop-types";
import Select, {components} from "react-select";
import classNames from "classnames";
import {useTranslation} from "react-i18next";
import take from "lodash/take";
import isArray from "lodash/isArray";
import concat from "lodash/concat";
import s from "./MentorFilters.pcss";
import {customStyles} from "../../../../shared/styles/filterCustomStyles";

const classMap = {
    0: s.left,
    1: s.middle,
    2: s.middle,
    3: s.right,
};

const presetFilterLabelMap = {
    country: "countries",
    degree: "areaOfStudy",
    degree_level: "degreeLevel",
    institution: "institution",
};

const Option = components.Option;

const MentorFilters = props => {
    const {t} = useTranslation(["mentorCard", "levels"]);
    const institutionFilter = props.filters.find(filter => filter.id === "institution");

    return (
        <div className={s.container}>
            {take(props.filters, institutionFilter ? 4 : 3).map((f, i) => {
                const filterLabel = presetFilterLabelMap[f.id]
                    ? t(presetFilterLabelMap[f.id], f.label)
                    : f.label;
                // translating options for degree level
                let options =
                    f.id === "degree_level"
                        ? f.options.map(({id, label}) => {
                              return {
                                  value: id,
                                  label: t(`levels:${label.toLowerCase()}`, label),
                              };
                          })
                        : f.options.map(({id, label}) => ({value: id, label}));

                /* Hack for handling multiple degrees having same name - a typical enterprise use case.
                 * We want a single option in the dropdown which has multiple degrees attached to it.
                 * For example: [
                 *   {"label": "Comp Sci", "value": "5f4e2f936ee5c5576e6b0539"},
                 *   {"label": "Full-Time MBA", "value": ["606cc93a6ee5c5c6ee3ca6de","612667376ee5c570afa36d2b"]},
                 *   {"label": "Maths","value": "6039046c6ee5c58e81a7659b"},
                 * ]
                 */
                if (f.id === "degree") {
                    const labelToValueMap = {};
                    options.forEach(({value, label}) => {
                        labelToValueMap[label] =
                            label in labelToValueMap
                                ? concat(labelToValueMap[label], value)
                                : value;
                    });
                    options = [];
                    for (const [k, v] of Object.entries(labelToValueMap)) {
                        options.push({label: k, value: v});
                    }
                }

                return (
                    <div key={f.id} className={classNames(s.filter, classMap[i])}>
                        <Select
                            noOptionsMessage={() => t("noOptionsMessage", "No options")}
                            id={`mentor-filter-${f.id}`}
                            data-test-id={f.id}
                            name={f.id}
                            placeholder={filterLabel}
                            options={options}
                            onChange={value => {
                                if (isArray(value) && value.length === 0) {
                                    return;
                                }
                                props.addFilter(f, value);
                            }}
                            clearable={false}
                            aria-label={filterLabel}
                            styles={customStyles}
                            components={{
                                Option: optionProps => (
                                    <Option
                                        {...optionProps}
                                        // adding classname for testing, since Option comp takes only few and ignores any custom prop
                                        className={`${
                                            optionProps.className
                                        } e2e-test-mentor-filter-${f.label}-option`}
                                    />
                                ),
                            }}
                            // Don't want the filter value to change, since we are actually managing filtering through
                            // the addFilter callback (adding filters) and filter pills (removing filters)
                            value={null}
                        />
                    </div>
                );
            })}
        </div>
    );
};

MentorFilters.propTypes = {
    // eslint-disable-next-line react/no-unused-prop-types
    addFilter: PropTypes.func.isRequired,
    filters: PropTypes.array,
};

MentorFilters.defaultProps = {
    filters: [],
};

export default MentorFilters;
